// Shared per-provider request queue (Phase: data-layer centralization).
// Every outbound market-data request in the app funnels through enqueue(), which
// serializes requests per bucket and enforces a minimum spacing between starts —
// so total request volume stays under each provider's ceiling no matter how many
// pollers/consumers fire at once. This is the root-cause fix for the rate-limit
// blank charts (docs/known-issue-rate-limiting-blank-charts.md); the 429
// retry-with-backoff in candles.js remains as a second line of defense.
//
// PRIORITY LANE (display charts): each bucket has two FIFO lanes. priority:true
// jobs (user-facing chart fetches) always dispatch before normal-lane jobs
// (scoring scan, candle cache, pollers), so a timeframe click never waits behind
// a scan burst. Spacing between request starts is enforced across BOTH lanes —
// total volume stays capped; only the ORDER changes.
//
// Buckets are keyed by provider/host, NOT by symbol — the limits are per-IP:
//   coinbase-exchange  api.exchange.coinbase.com  (public: ~10 req/s)  -> 150ms
//   coinbase-spot      api.coinbase.com           (generous)           -> 100ms
//   alpaca             Alpaca data API            (200 req/min)        -> 350ms (~170/min)
const BUCKET_SPACING_MS = {
  "coinbase-exchange": 150,
  "coinbase-spot": 100,
  "alpaca": 350,
};
const DEFAULT_SPACING_MS = 200;

// bucket -> { high: [job], low: [job], running: bool, lastStart: ms epoch }
const buckets = {};
const getBucket = (name) =>
  buckets[name] || (buckets[name] = { high: [], low: [], running: false, lastStart: 0 });

// Enqueue fn (a () => Promise) on the bucket's queue. opts.priority = true puts it
// in the display-chart lane (dispatched first). Returns a promise that settles
// exactly as fn's request does. A rejected request never poisons the queue — the
// pump catches per-job and keeps draining.
export function enqueue(bucket, fn, opts = {}) {
  const b = getBucket(bucket);
  return new Promise((resolve, reject) => {
    (opts.priority ? b.high : b.low).push({ fn, resolve, reject });
    pump(bucket);
  });
}

async function pump(name) {
  const b = getBucket(name);
  if (b.running) return;
  b.running = true;
  const spacing = BUCKET_SPACING_MS[name] ?? DEFAULT_SPACING_MS;
  while (b.high.length || b.low.length) {
    const job = b.high.length ? b.high.shift() : b.low.shift();
    const wait = b.lastStart + spacing - Date.now();
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    b.lastStart = Date.now();
    // Fire WITHOUT awaiting completion: rate limits cap request STARTS per second,
    // not concurrency — so only the starts are spaced, and in-flight latencies
    // overlap. (Previously the queue awaited each response before dispatching the
    // next, serializing latency: 4 chart pages = 4 full round-trips end-to-end.)
    try {
      job.fn().then(job.resolve, job.reject);
    } catch (e) {
      job.reject(e);
    }
  }
  b.running = false;
}
